import { useCallback } from 'react';
import { logger } from '../../util/logger';
import {
  useGetUserPreference,
  useSetUserPreferenceLazy,
} from './preference.hooks';

const BOOKMARK_KEY = 'favorite.bookmarks';

export enum BookmarkType {
  TEAM = 'team',
  CHAT = 'chat',
  MACHINE = 'machine',
  SCRATCHPAD = 'scratchpad',
}

export interface Bookmark {
  url: string;
  title: string;
  type: BookmarkType;
}

export const useListBookmarks = (): Bookmark[] => {
  const bookmarkPreference = useGetUserPreference<Bookmark[]>(BOOKMARK_KEY);
  return bookmarkPreference?.value ?? [];
};

export const useToggleBookmark = (): ((bookmark: Bookmark) => void) => {
  const bookmarks = useListBookmarks();
  const [setUserPreference] = useSetUserPreferenceLazy<Bookmark[]>();

  const toggleBookmark = useCallback(
    async (bookmark: Bookmark) => {
      const filteredBookmarks = bookmarks.filter((b) => b.url !== bookmark.url);

      if (
        bookmarks.length > 0 &&
        filteredBookmarks.length !== bookmarks.length
      ) {
        logger.info('removing bookmark', {
          bookmark,
        });
        return setUserPreference({
          key: BOOKMARK_KEY,
          value: filteredBookmarks,
        });
      } else {
        logger.info('adding bookmark', {
          bookmark,
        });
        return setUserPreference({
          key: BOOKMARK_KEY,
          value: [...bookmarks, bookmark],
        });
      }
    },
    [bookmarks, setUserPreference]
  );

  return toggleBookmark;
};

export const useUpdateBookmark = (): ((bookmark: Bookmark) => void) => {
  const bookmarks = useListBookmarks();
  const [setUserPreference] = useSetUserPreferenceLazy<Bookmark[]>();

  const updateBookmark = useCallback(
    async (bookmark: Bookmark) => {
      const filteredBookmarks = bookmarks.filter((b) => b.url !== bookmark.url);
      logger.info('updating bookmark', {
        bookmark,
      });
      return setUserPreference({
        key: BOOKMARK_KEY,
        value: [...filteredBookmarks, bookmark],
      });
    },
    [bookmarks, setUserPreference]
  );

  return updateBookmark;
};

export const useIsBookmarked = (path: string): [boolean, Bookmark?] => {
  const listBookmarks = useListBookmarks();

  const existingBookmark = listBookmarks.find((b) => b.url === path);

  return [existingBookmark ? true : false, existingBookmark];
};
